import { differenceInDays, differenceInHours, differenceInMinutes, differenceInMonths, differenceInYears, subMonths, subYears } from "date-fns";
/**
 * Formats the date to post-like date representation
 *
 * @param {string} date in ISO 8601 format
 *
 * @returns {string} formatted date
 */
export var toPostDateTimeFormat = function (currentDate, date) {
    return "".concat(toDateTimeFormat(date), ", ").concat(timeSinceNow(currentDate, date));
};
export var remainingTime = function (futureDate) {
    var result = [];
    var now = new Date();
    var future = futureDate ? new Date(futureDate) : new Date();
    var years = differenceInYears(future, now);
    if (years > 0) {
        result.push("".concat(years, " years"));
        future = subYears(future, years);
    }
    var months = differenceInMonths(future, now);
    if (months > 0) {
        result.push("".concat(months, " months"));
        future = subMonths(future, months);
    }
    var days = differenceInDays(future, now);
    if (days > 0) {
        result.push("".concat(days, " ").concat(days === 1 ? "day" : "days"));
    }
    var hours = differenceInHours(future, now);
    if (days <= 0 && months <= 0 && years <= 0) {
        result.push("".concat(hours, " hours"));
    }
    var minutes = differenceInMinutes(future, now);
    if (hours <= 0 && days <= 0 && months <= 0 && years <= 0) {
        result.push("".concat(minutes, " minutes"));
    }
    return result.join(" ");
};
/**
 * Formats the date in {@link https://en.wikipedia.org/wiki/ISO_8601|ISO 8601} format to its local time representation
 *
 * @example
 * const date = '2021-03-23T12:32:41.449Z';
 * toDateTimeFormat(date);
 * // returns '1:32:41 PM';
 *
 * @param {string} date in ISO 8601 format
 *
 * @returns {string} formatted local time format
 */
export var toDateTimeFormat = function (date) {
    isDateValid(date);
    return new Intl.DateTimeFormat("en-GB", {
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        timeZoneName: "short"
    })
        .format(new Date(date))
        .slice(0, -6)
        .toLocaleUpperCase();
};
/**
 * Returns date, sample output '23 Feb 2019'
 *
 * @param {string} date in ISO 8601 format
 *
 * @returns {string} formatted date
 */
export var toLongDateFormat = function (date) {
    var year = new Intl.DateTimeFormat("en", { year: "numeric" }).format(new Date(date));
    return "".concat(toDateWithoutYearFormat(date), " ").concat(year);
};
/**
 * Returns date without year, sample output '23 Feb'
 *
 * @param {string} date in ISO 8601 format
 *
 * @returns {string} formatted date
 */
export var toDateWithoutYearFormat = function (date) {
    var formattedDate = new Date(date);
    var month = new Intl.DateTimeFormat("en", { month: "short" }).format(formattedDate);
    var day = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(formattedDate);
    return "".concat(day, " ").concat(month);
};
/**
 * Returns date without year, sample output '23 Feb'
 *
 * @param {string} date in ISO 8601 format
 *
 * @returns {string} formatted date
 */
export var toLongDateWithoutYearFormat = function (date) {
    var formattedDate = new Date(date);
    var month = new Intl.DateTimeFormat("en", { month: "long" }).format(formattedDate);
    var day = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(formattedDate);
    return "".concat(month, " ").concat(day);
};
var DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
var WEEK_IN_MILLISECONDS = 7 * DAY_IN_MILLISECONDS;
var YEAR_IN_MILLISECONDS = 365 * DAY_IN_MILLISECONDS;
/**
 * Returns the relative time from today
 *
 * @param {string} date in ISO 8601 format
 *
 * @returns {string} formatted date
 */
export var timeSinceNow = function (currentDate, date) {
    isDateValid(date);
    var timeDifference = currentDate - Date.parse(date);
    if (timeDifference < DAY_IN_MILLISECONDS)
        return "Today";
    if (timeDifference < DAY_IN_MILLISECONDS * 2)
        return "Yesterday";
    if (timeDifference < WEEK_IN_MILLISECONDS)
        return "".concat(Math.round(timeDifference / DAY_IN_MILLISECONDS), " days ago");
    if (timeDifference < YEAR_IN_MILLISECONDS)
        return toDateWithoutYearFormat(date);
    return toLongDateFormat(date);
};
/**
 * Throws Error if the date has an invalid format
 *
 * @param {string} date in ISO 8601 format
 */
var isDateValid = function (date) {
    var dateFormat = new Date(date);
    if (Object.prototype.toString.call(dateFormat) !== "[object Date]" || isNaN(dateFormat.getTime()))
        throw new Error("The provided string has a invalid date format");
};
/**
 * Returns the date in format DD.MM.YYYY
 *
 * @param {string} date in ISO 8601 format
 *
 * @returns {string} formatted date
 */
export var toDateFormat = function (date) {
    return Intl.DateTimeFormat("en-GB", { month: "2-digit", day: "2-digit", year: "numeric" }).format(new Date(date));
};
export var getMonthName = function (idx) {
    var objDate = new Date();
    objDate.setDate(1);
    objDate.setMonth(idx - 1);
    var locale = "en-GB", month = objDate.toLocaleString(locale, { month: "long" });
    return month;
};
export var timeAgoPostNotificationsFormatter = function (value, unit) {
    if (unit === "second") {
        if (value < 45) {
            return "now";
        }
        if (value < 90) {
            return "1 minute ago";
        }
    }
    if (unit === "minute") {
        if (value < 2) {
            return "".concat(value, " minute ago");
        }
        if (value < 45) {
            return "".concat(value, " minutes ago");
        }
        if (value < 90) {
            return "~1 hour ago";
        }
    }
    if (unit === "hour") {
        if (value < 2) {
            return "".concat(value, " hour ago");
        }
        if (value < 24) {
            return "".concat(value, " hours ago");
        }
    }
    if (unit === "day") {
        if (value < 2) {
            return "".concat(value, " day ago");
        }
        if (value < 30) {
            return "".concat(value, " days ago");
        }
    }
    if (unit === "week") {
        if (value < 2) {
            return "".concat(value, " week ago");
        }
        if (value < 30) {
            return "".concat(value, " weeks ago");
        }
    }
    if (unit === "month") {
        if (value < 2) {
            return "1 month ago";
        }
        if (value < 12) {
            return "".concat(value, " months ago");
        }
    }
    if (unit === "year") {
        if (value < 2) {
            return "1 year ago";
        }
        return "".concat(value, " years ago");
    }
    return "";
};
export function formattedTimeFromSeconds(seconds) {
    // Display hours, minutes and seconds from seconds like 1:00 from 60 seconds without leading zeros and using substr
    var hours = Math.floor(seconds / 3600);
    var minutes = Math.floor((seconds - hours * 3600) / 60);
    var secondsLeft = seconds - hours * 3600 - minutes * 60;
    var secondsLeftWithLeadingZero = secondsLeft < 10 ? "0".concat(secondsLeft) : "".concat(secondsLeft);
    return "".concat(hours > 0 ? "".concat(hours, ":") : "").concat(minutes > 0 ? "".concat(minutes, ":") : "0:").concat(secondsLeftWithLeadingZero);
}
//# sourceMappingURL=data:application/json;base64,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